import { useEffect } from "preact/hooks";
import { isAuthenticated } from "../store/auth.js";
import PublicFooter from "../components/PublicFooter.js";


const platformLimits = [
  { name: "Zapier", limit: "6 MB" },
  { name: "Make", limit: "5–10 MB" },
  { name: "n8n", limit: "16 MB" },
  { name: "Workato", limit: "varies" },
];

const heroSignals = [
  { label: "Payloads", value: "Up to 250 MB" },
  { label: "Static IP", value: "Platform Static IP" },
  { label: "Free tier", value: "5 events / month" },
  { label: "Activation", value: "$25 one time" },
];

const painPoints = [
  {
    title: "Automation platforms choke on file-sized payloads",
    body:
      "Product photos, PDFs, signed forms, and document bundles do not fit inside normal webhook limits. The workflow fails before it even starts.",
  },
  {
    title: "Security reviews stall on rotating cloud IPs",
    body:
      "When a sender or downstream system demands an allowlist, a moving target breaks approval. webhookstorage gives you one stable ingress IP.",
  },
  {
    title: "Homegrown proxies become permanent infrastructure",
    body:
      "The quick fix turns into a Lambda, a bucket, retry logic, auth checks, event history, and incident ownership. This product exists so you do not have to build that.",
  },
];

const steps = [
  {
    num: "01",
    title: "Receive the large payload",
    body:
      "Point the upstream sender at your webhookstorage endpoint. We accept payloads up to 250 MB with a static IP you can allowlist.",
  },
  {
    num: "02",
    title: "Store it durably",
    body:
      "The raw body is written to S3 with tenant isolation, encrypted at rest, and tracked as an event you can inspect or audit later.",
  },
  {
    num: "03",
    title: "Let automation fetch the body on demand",
    body:
      "Your workflow receives a lightweight JSON notification with a signed retrieval link. The platform stays fast because it only downloads the payload when needed.",
  },
];

const featureCards = [
  {
    title: "Static IP for allowlists",
    body:
      "Use one fixed ingress address instead of chasing cloud ranges through procurement and firewall change requests.",
    icon: "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z",
  },
  {
    title: "Large payload buffer",
    body:
      "Accept bodies up to 250 MB. The payload goes to S3; your workflow receives a compact event reference instead of the full blob.",
    icon: "M20 6L9 17l-5-5",
  },
  {
    title: "Signed retrieval links",
    body:
      "Downstream systems fetch the payload through short-lived presigned URLs. No extra API credentials are required for the download itself.",
    icon: "M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1",
  },
  {
    title: "Per-endpoint isolation",
    body:
      "Each workflow gets its own endpoint, secret, outbound URL, and event history. Keep client data and operational boundaries clean.",
    icon: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10",
  },
  {
    title: "Event history and retrieval",
    body:
      "Every ingest event is logged with timestamp, payload size, content type, and sender IP so operators can debug, re-fetch, or audit without guessing.",
    icon: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15",
  },
  {
    title: "Per-endpoint authentication",
    body:
      "Validate every inbound request with endpoint-level secrets, then add your own network controls around the static IP for defense in depth.",
    icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
  },
];

const pricingTiers = [
  {
    name: "Free",
    headline: "5 events / month",
    detail: "No credit card required. Enough to prove the loop end-to-end.",
    highlight: false,
    bullets: [
      "Same endpoints, signed links, and static IP as paid accounts",
      "1-day webhook retention, resets monthly",
      "Best for your first live round trip and small-volume testing",
    ],
    ctaLabel: "Start free",
  },
  {
    name: "Activated",
    headline: "$25 one-time activation",
    detail:
      "$25 becomes account credit. After that: $0.03 per webhook and $0.10 per GB-month.",
    highlight: true,
    bullets: [
      "Unlocks 7-day webhook retention",
      "Metered usage starts only after the included credit is consumed",
      "No subscription math, no seats, no plan migration ceremony",
    ],
    ctaLabel: "Start free, activate later",
  },
  {
    name: "Business",
    headline: "Custom onboarding",
    detail: "For higher volume, custom retention, procurement, and support.",
    highlight: false,
    bullets: [
      "Commercial terms for larger deployments",
      "Longer retention windows and higher ingest caps",
      "Dedicated rollout support and procurement help",
    ],
    cta: "https://calendar.app.google/XdnNT3AGfjGqFRi49",
    ctaLabel: "Book a call",
  },
];

const useCases = [
  {
    persona: "Operations",
    scenario: "Route oversized Shopify payloads into Make",
    detail:
      "Product images, order documents, and multi-item payloads hit Make before the flow can parse them. webhookstorage stores the heavy body and hands Make a fetchable link instead.",
  },
  {
    persona: "Security",
    scenario: "Pass inbound review with a fixed IP",
    detail:
      "A sender or downstream system requires an allowlist before anything can go live. webhookstorage gives you one ingress IP and endpoint auth from day one.",
  },
  {
    persona: "Agencies",
    scenario: "Separate endpoints by client and workflow",
    detail:
      "Keep per-client secrets, destinations, and event histories isolated without standing up custom proxy infrastructure for every account.",
  },
  {
    persona: "Media & AI",
    scenario: "Feed recordings and media into automation workflows",
    detail:
      "Screen recordings, audio files, and video clips up to 250 MB can be captured via webhook and routed into transcription, analysis, or archival workflows. Your automation platform fetches the media on demand via a signed link.",
  },
];

const faqs = [
  {
    q: "What exactly gets stored?",
    a: "The raw webhook body, unchanged. webhookstorage also keeps event metadata like timestamp, payload size, content type, and sender IP so operators can inspect and audit delivery behavior.",
  },
  {
    q: "Why is the static IP important?",
    a: "Security teams need something specific to allowlist. A single stable IP avoids the usual back-and-forth around rotating cloud ranges and custom reverse proxies.",
  },
  {
    q: "How does the free tier work?",
    a: "You get 5 events per month with 1-day retention, forever. It uses the same core product surface as activated accounts, so you can validate the full ingest-to-fetch workflow before paying anything.",
  },
  {
    q: "What happens after I activate for $25?",
    a: "That $25 becomes account credit immediately. Once the included credit is consumed, usage is metered at $0.03 per webhook and $0.10 per GB-month of retained storage.",
  },
  {
    q: "Which platforms does this work with?",
    a: "Any platform that can accept an HTTP POST and later fetch a URL. Zapier, Make, n8n, Workato, custom workers, and internal services all fit that model.",
  },
  {
    q: "What if my automation misses the signed link the first time?",
    a: "The payload remains in storage for your retention window. You can re-fetch it through the dashboard or API at any time before it expires.",
  },
];

function FeatureIcon({ path }: { path: string }) {
  return (
    <svg
      class="h-6 w-6 shrink-0 text-emerald-700"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      stroke-width="2"
    >
      <path stroke-linecap="round" stroke-linejoin="round" d={path} />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      class="mt-0.5 h-5 w-5 shrink-0 text-emerald-700"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      stroke-width="2"
    >
      <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

export default function Home() {
  useEffect(() => {
    document.title =
      "webhookstorage — Large payload webhook storage with a static IP";

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }

    meta.setAttribute(
      "content",
      "Receive large webhook payloads up to 250 MB, store them in S3, and deliver lightweight signed-link events to your automation platform. Static IP included. Free tier: 5 events per month. Activate for $25 when you need volume.",
    );

    const jsonLd = document.createElement("script");
    jsonLd.type = "application/ld+json";
    jsonLd.textContent = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "webhookstorage",
        url: window.location.origin,
        description:
          "Large payload webhook storage with a static IP, S3-backed storage, and signed retrieval links.",
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ]);

    document.head.appendChild(jsonLd);

    return () => {
      jsonLd.remove();
    };
  }, []);

  const primaryHref = isAuthenticated.value ? "/dashboard" : "/login";
  const primaryLabel = isAuthenticated.value
    ? "Open dashboard"
    : "Start free";

  return (
    <div class="min-h-screen bg-[radial-gradient(circle_at_top_left,_#ecfdf5,_transparent_36%),radial-gradient(circle_at_top_right,_#fef3c7,_transparent_28%),linear-gradient(to_bottom,_#f8fafc,_#ffffff_30%,_#f8fafc)] text-slate-900">
      <header class="border-b border-slate-200/60 bg-white/90 backdrop-blur-sm">
        <div class="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <a href="/" class="text-lg font-bold tracking-tight text-slate-950">
            webhookstorage
          </a>
          <nav class="flex items-center gap-4 text-sm">
            <a
              href="#how-it-works"
              class="hidden text-slate-600 hover:text-slate-950 sm:inline"
            >
              How it works
            </a>
            <a
              href="#pricing"
              class="hidden text-slate-600 hover:text-slate-950 sm:inline"
            >
              Pricing
            </a>
            <a
              href="#faq"
              class="hidden text-slate-600 hover:text-slate-950 md:inline"
            >
              FAQ
            </a>
            <a
              href="/guides"
              class="hidden text-slate-600 hover:text-slate-950 sm:inline"
            >
              Guides
            </a>
            <a
              href="/docs"
              class="hidden text-slate-600 hover:text-slate-950 sm:inline"
            >
              Docs
            </a>
            <a
              href="/blog"
              class="hidden text-slate-600 hover:text-slate-950 md:inline"
            >
              Blog
            </a>
            <a
              href="/login"
              class="rounded-full border border-slate-300 px-4 py-2 font-medium text-slate-950 transition hover:border-slate-400 hover:bg-slate-50"
            >
              Sign in
            </a>
          </nav>
        </div>
      </header>

      <main>
        <section class="mx-auto max-w-6xl px-4 pb-16 pt-12 sm:px-6 sm:pt-16 lg:px-8 lg:pb-20 lg:pt-20">
          <div class="grid items-start gap-10 lg:grid-cols-[minmax(0,1fr)_24rem]">
            <div>
              <div class="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-900">
                <span class="inline-block h-2 w-2 rounded-full bg-emerald-600" />
                Large payload webhook storage for automation workflows
              </div>

              <h1 class="mt-6 max-w-4xl text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl lg:text-6xl">
                Your webhooks are too large for{" "}
                <span class="text-emerald-800">Zapier, Make, and n8n.</span>
              </h1>

              <p class="mt-6 max-w-3xl text-lg leading-8 text-slate-600">
                webhookstorage sits between the sender and your automation platform.
                It accepts bodies up to 250 MB, stores them durably in S3, and
                forwards a compact event with a signed retrieval link. You keep the
                payload, the audit trail, and the static IP without building a proxy.
              </p>

              <div class="mt-6 flex flex-wrap gap-3 text-sm text-slate-600">
                {platformLimits.map((platform) => (
                  <span
                    key={platform.name}
                    class="rounded-full border border-slate-200 bg-white/80 px-3 py-1"
                  >
                    {platform.name} limit: {platform.limit}
                  </span>
                ))}
              </div>

              <div class="mt-8 flex flex-wrap gap-3">
                <a
                  href={primaryHref}
                  class="rounded-full bg-emerald-700 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-800"
                >
                  {primaryLabel}
                </a>
                <a
                  href="#pricing"
                  class="rounded-full border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                >
                  See pricing
                </a>
              </div>

              <div class="mt-10 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {heroSignals.map((signal) => (
                  <div
                    key={signal.label}
                    class="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm"
                  >
                    <p class="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                      {signal.label}
                    </p>
                    <p class="mt-2 text-lg font-semibold text-slate-950">
                      {signal.value}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <aside class="rounded-3xl border border-slate-200 bg-slate-950 p-6 text-white shadow-xl">
              <p class="text-xs font-semibold uppercase tracking-[0.22em] text-amber-300">
                Operator Brief
              </p>
              <h2 class="mt-3 text-2xl font-semibold">
                Ship the first production-safe webhook path fast.
              </h2>

              <div class="mt-6 space-y-4">
                <div class="rounded-2xl bg-white/5 p-4 ring-1 ring-white/10">
                  <p class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                    Static IP
                  </p>
                  <p class="mt-2 text-lg font-semibold text-white">Platform Static IP</p>
                  <p class="mt-2 text-sm leading-6 text-slate-300">
                    Give security and vendors one concrete allowlist target.
                    Visible in your dashboard after sign-up.
                  </p>
                </div>

                <div class="rounded-2xl bg-white/5 p-4 ring-1 ring-white/10">
                  <p class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                    First payment
                  </p>
                  <p class="mt-2 text-lg font-semibold text-white">$25 activates billing</p>
                  <p class="mt-2 text-sm leading-6 text-slate-300">
                    That same $25 is applied as account credit before metered
                    charges kick in.
                  </p>
                </div>

                <div class="rounded-2xl bg-emerald-500/10 p-4 ring-1 ring-emerald-400/30">
                  <p class="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-200">
                    Best-fit jobs
                  </p>
                  <ul class="mt-3 space-y-2 text-sm leading-6 text-emerald-50">
                    <li>Large media payloads</li>
                    <li>Signed documents and form bundles</li>
                    <li>Security-reviewed inbound webhook flows</li>
                    <li>Per-client endpoint isolation</li>
                  </ul>
                </div>
              </div>
            </aside>
          </div>
        </section>

        <section class="border-y border-slate-200/70 bg-white/70 py-16">
          <div class="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <p class="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-700">
              Why teams buy this
            </p>
            <h2 class="mt-3 max-w-3xl text-3xl font-bold tracking-tight text-slate-950">
              The problem is not webhook delivery. The problem is what happens when
              the payload is too large for the tools around it.
            </h2>
            <div class="mt-10 grid gap-6 lg:grid-cols-3">
              {painPoints.map((item) => (
                <article
                  key={item.title}
                  class="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
                >
                  <h3 class="text-lg font-semibold text-slate-950">{item.title}</h3>
                  <p class="mt-3 text-sm leading-7 text-slate-600">{item.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="how-it-works" class="py-16">
          <div class="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <p class="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-700">
              How it works
            </p>
            <h2 class="mt-3 max-w-2xl text-3xl font-bold tracking-tight text-slate-950">
              Put a durable storage hop between the sender and your workflow engine.
            </h2>
            <div class="mt-10 grid gap-6 md:grid-cols-3">
              {steps.map((step) => (
                <article
                  key={step.num}
                  class="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
                >
                  <p class="text-xs font-semibold uppercase tracking-[0.2em] text-amber-700">
                    Step {step.num}
                  </p>
                  <h3 class="mt-3 text-xl font-semibold text-slate-950">
                    {step.title}
                  </h3>
                  <p class="mt-3 text-sm leading-7 text-slate-600">{step.body}</p>
                </article>
              ))}
            </div>

            <div class="mt-10 rounded-3xl border border-slate-200 bg-slate-950 p-6 text-slate-200 shadow-lg">
              <div class="grid gap-4 md:grid-cols-[1fr_auto_1fr_auto_1fr] md:items-center">
                <div class="rounded-2xl bg-white/5 p-4">
                  Sender POSTs the large body
                </div>
                <div class="hidden text-center text-slate-500 md:block">→</div>
                <div class="rounded-2xl bg-emerald-500/15 p-4 ring-1 ring-emerald-400/30">
                  webhookstorage stores to S3
                </div>
                <div class="hidden text-center text-slate-500 md:block">→</div>
                <div class="rounded-2xl bg-white/5 p-4">
                  Your platform receives a signed-link event
                </div>
              </div>
            </div>
          </div>
        </section>

        <section class="border-y border-slate-200/70 bg-white/70 py-16">
          <div class="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <p class="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-700">
              What you get
            </p>
            <h2 class="mt-3 text-3xl font-bold tracking-tight text-slate-950">
              Enough operator leverage to stop building this yourself.
            </h2>
            <div class="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {featureCards.map((feature) => (
                <article
                  key={feature.title}
                  class="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
                >
                  <FeatureIcon path={feature.icon} />
                  <h3 class="mt-4 text-lg font-semibold text-slate-950">
                    {feature.title}
                  </h3>
                  <p class="mt-3 text-sm leading-7 text-slate-600">{feature.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="pricing" class="py-16">
          <div class="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <p class="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-700">
              Pricing
            </p>
            <h2 class="mt-3 max-w-3xl text-3xl font-bold tracking-tight text-slate-950">
              Start with 5 free events per month. Activate once for $25 when the
              workflow is real.
            </h2>
            <p class="mt-4 max-w-2xl text-base leading-7 text-slate-600">
              The product is designed to prove itself cheaply, then stay simple
              when you move into production. No annual seat planning. No forced
              enterprise conversation just to handle a large payload.
            </p>

            <div class="mt-10 grid gap-6 lg:grid-cols-3">
              {pricingTiers.map((tier) => (
                <article
                  key={tier.name}
                  class={`rounded-3xl border p-6 shadow-sm ${
                    tier.highlight
                      ? "border-emerald-300 bg-emerald-50/80 ring-1 ring-emerald-200"
                      : "border-slate-200 bg-white"
                  }`}
                >
                  <p
                    class={`text-xs font-semibold uppercase tracking-[0.22em] ${
                      tier.highlight ? "text-emerald-700" : "text-slate-500"
                    }`}
                  >
                    {tier.name}
                  </p>
                  <h3 class="mt-4 text-3xl font-bold text-slate-950">
                    {tier.headline}
                  </h3>
                  <p class="mt-3 text-sm leading-6 text-slate-600">{tier.detail}</p>
                  <ul class="mt-6 space-y-3 text-sm leading-6 text-slate-700">
                    {tier.bullets.map((bullet) => (
                      <li key={bullet} class="flex gap-3">
                        <CheckIcon />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                  {"cta" in tier ? (
                    <a
                      href={(tier as { cta: string }).cta}
                      class="mt-6 inline-block rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                    >
                      {(tier as { ctaLabel: string }).ctaLabel}
                    </a>
                  ) : (
                    <a
                      href={primaryHref}
                      class={`mt-6 inline-block rounded-full px-5 py-3 text-sm font-semibold transition ${
                        tier.highlight
                          ? "bg-emerald-700 text-white hover:bg-emerald-800"
                          : "border border-slate-300 text-slate-900 hover:bg-slate-50"
                      }`}
                    >
                      {tier.ctaLabel}
                    </a>
                  )}
                </article>
              ))}
            </div>

            <div class="mt-6 rounded-3xl bg-slate-950 px-6 py-5 text-sm leading-7 text-slate-300">
              <span class="font-semibold text-white">Included across the product:</span>{" "}
              static IP ingress, S3-backed payload storage, endpoint-level auth,
              signed retrieval links, event history, and guides for Zapier,
              Make, and n8n.
            </div>
          </div>
        </section>

        <section class="border-y border-slate-200/70 bg-white/70 py-16">
          <div class="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <p class="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-700">
              Use cases
            </p>
            <h2 class="mt-3 text-3xl font-bold tracking-tight text-slate-950">
              Built for production paths, not toy demos.
            </h2>
            <div class="mt-10 grid gap-6 lg:grid-cols-3">
              {useCases.map((item) => (
                <article
                  key={item.scenario}
                  class="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
                >
                  <p class="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">
                    {item.persona}
                  </p>
                  <h3 class="mt-3 text-lg font-semibold text-slate-950">
                    {item.scenario}
                  </h3>
                  <p class="mt-3 text-sm leading-7 text-slate-600">{item.detail}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="faq" class="py-16">
          <div class="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <p class="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-700">
              FAQ
            </p>
            <h2 class="mt-3 text-3xl font-bold tracking-tight text-slate-950">
              The short answers.
            </h2>
            <div class="mt-10 grid gap-4 lg:grid-cols-2">
              {faqs.map((faq) => (
                <article
                  key={faq.q}
                  class="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
                >
                  <h3 class="text-base font-semibold text-slate-950">{faq.q}</h3>
                  <p class="mt-3 text-sm leading-7 text-slate-600">{faq.a}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section class="pb-16">
          <div class="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div class="rounded-[2rem] bg-slate-950 px-8 py-12 text-center shadow-xl sm:px-12">
              <h2 class="text-3xl font-bold text-white">
                Get the first full payload through without building a proxy.
              </h2>
              <p class="mx-auto mt-4 max-w-2xl text-base leading-7 text-slate-400">
                Create an endpoint, send a real webhook, inspect the stored event,
                and prove the downstream fetch flow. Start free. Activate for $25
                only when the volume justifies it.
              </p>
              <div class="mt-8 flex flex-wrap justify-center gap-3">
                <a
                  href={primaryHref}
                  class="rounded-full bg-emerald-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-emerald-500"
                >
                  {primaryLabel}
                </a>
                <a
                  href="/docs"
                  class="rounded-full border border-slate-600 px-6 py-3 text-sm font-semibold text-slate-300 transition hover:border-slate-400 hover:text-white"
                >
                  Read the API docs
                </a>
              </div>
            </div>
          </div>
        </section>

        <PublicFooter />
      </main>
    </div>
  );
}
